import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-scroll';

const navLinks = [
  { label: 'Home', to: 'home' },
  { label: 'About', to: 'about' },
  { label: 'Skills', to: 'skill' },
  { label: 'Services', to: 'service' },
  { label: 'Projects', to: 'portfolio' },
  { label: 'Reviews', to: 'testimonial' },
  { label: 'Contact', to: 'contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const closeMenu = () => setMenuOpen(false);

  return (
    <>
      <motion.nav
        initial={{ y: -70, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.65, ease: [0.16, 1, 0.3, 1] }}
        style={{
          position: 'fixed',
          top: 18,
          left: 0,
          right: 0,
          margin: '0 auto',
          zIndex: 1000,
          width: 'min(1120px, calc(100% - 32px))',
          minHeight: 62,
          padding: '9px 10px 9px 16px',
          background: scrolled
            ? 'linear-gradient(135deg, rgba(18,21,28,0.78), rgba(18,21,28,0.45))'
            : 'linear-gradient(135deg, rgba(255,255,255,0.13), rgba(255,255,255,0.045))',
          backdropFilter: 'blur(24px) saturate(150%)',
          WebkitBackdropFilter: 'blur(24px) saturate(150%)',
          border: '1px solid rgba(255,255,255,0.18)',
          borderRadius: 8,
          boxShadow: scrolled ? '0 22px 70px rgba(0,0,0,0.34)' : '0 18px 50px rgba(0,0,0,0.18)',
          transition: 'background 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 18,
          overflow: 'hidden',
        }}
      >
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: 8,
            pointerEvents: 'none',
            background: 'linear-gradient(90deg, rgba(54,234,211,0.18), transparent 28%, rgba(255,77,141,0.14) 64%, rgba(255,209,102,0.16))',
            opacity: scrolled ? 0.38 : 0.56,
          }}
        />
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: 12,
            right: 12,
            bottom: 0,
            height: 1,
            background: 'linear-gradient(90deg, transparent, rgba(54,234,211,0.75), rgba(255,77,141,0.65), transparent)',
            pointerEvents: 'none',
          }}
        />
        <Link
          to="home"
          href="#home"
          spy
          smooth
          offset={-80}
          duration={600}
          style={{ position: 'relative', zIndex: 1, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}
          onClick={closeMenu}
        >
          <span
            style={{
              width: 38,
              height: 38,
              borderRadius: 8,
              display: 'grid',
              placeItems: 'center',
              background: 'linear-gradient(135deg, rgba(54,234,211,0.95), rgba(255,77,141,0.9), rgba(255,209,102,0.95))',
              color: 'var(--ink)',
              fontFamily: 'var(--font-display)',
              fontWeight: 800,
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.55), 0 12px 28px rgba(0,0,0,0.25)',
            }}
          >
            P
          </span>
          <span style={{ fontFamily: 'var(--font-display)', fontWeight: 800, color: 'var(--white)', fontSize: 19 }}>
            Prakash.C
          </span>
        </Link>

        <div className="desktop-nav" style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: 22 }}>
          {navLinks.map(link => (
            <Link
              key={link.to}
              to={link.to}
              href={`#${link.to}`}
              spy
              smooth
              offset={-80}
              duration={600}
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 1.2,
                textTransform: 'uppercase',
                color: 'rgba(247,247,251,0.74)',
                cursor: 'pointer',
                textDecoration: 'none',
                padding: '8px 0',
              }}
              activeStyle={{ color: 'var(--aqua)' }}
            >
              {link.label}
            </Link>
          ))}
          <a href="mailto:prakash12991299@gmail.com" className="btn-primary" style={{ minHeight: 42, padding: '10px 18px', fontSize: 11 }}>
            Hire Me
          </a>
        </div>

        <button
          className="hamburger"
          onClick={() => setMenuOpen(open => !open)}
          aria-label="Toggle menu"
          aria-expanded={menuOpen}
          style={{
            position: 'relative',
            zIndex: 1,
            display: 'none',
            width: 44,
            height: 44,
            border: '1px solid rgba(255,255,255,0.18)',
            background: 'rgba(255,255,255,0.08)',
            cursor: 'pointer',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            gap: 5,
          }}
        >
          {[0, 1, 2].map(i => (
            <motion.span
              key={i}
              style={{ width: 20, height: 2, background: 'var(--white)', borderRadius: 8 }}
              animate={menuOpen
                ? i === 0 ? { rotate: 45, y: 7 }
                : i === 1 ? { opacity: 0 }
                : { rotate: -45, y: -7 }
                : { rotate: 0, y: 0, opacity: 1 }}
            />
          ))}
        </button>
      </motion.nav>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            style={{
              position: 'fixed',
              inset: '96px 16px auto 16px',
              zIndex: 999,
              padding: 20,
              background: 'linear-gradient(135deg, rgba(15,18,24,0.82), rgba(15,18,24,0.58))',
              border: '1px solid rgba(255,255,255,0.18)',
              borderRadius: 8,
              backdropFilter: 'blur(24px) saturate(150%)',
              WebkitBackdropFilter: 'blur(24px) saturate(150%)',
              boxShadow: 'var(--shadow)',
            }}
          >
            <div style={{ display: 'grid', gap: 8 }}>
              {navLinks.map((link, i) => (
                <motion.div
                  key={link.to}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.04 }}
                >
                  <Link
                    to={link.to}
                    href={`#${link.to}`}
                    spy
                    smooth
                    offset={-80}
                    duration={600}
                    onClick={closeMenu}
                    style={{
                      display: 'block',
                      padding: '13px 12px',
                      color: 'var(--white)',
                      textDecoration: 'none',
                      fontFamily: 'var(--font-display)',
                      fontWeight: 800,
                      letterSpacing: 0,
                      cursor: 'pointer',
                    }}
                  >
                    {link.label}
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        @media (max-width: 1020px) {
          .desktop-nav { display: none !important; }
          .hamburger { display: flex !important; }
        }
      `}</style>
    </>
  );
}
